import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const ThemeContext = createContext(null);

export const DEFAULT_THEME = {
  brand: "#CC0033",
  brand_hover: "#99001F",
  background: "#F5F4F2",
  text: "#1D1B19",
  login_bg: "#cc0033",
  login_text: "#ffffff",
  logo: "",
  app_version: "1.0",
};

// Valori dei vecchi default: nella grafica interna vengono letti come i nuovi default
const LEGACY = { brand_hover: "#a30028", background: "#ffffff", text: "#09090b" };

function inner(t) {
  const out = { ...t };
  Object.entries(LEGACY).forEach(([k, v]) => {
    if (!out[k] || String(out[k]).toLowerCase() === v) out[k] = DEFAULT_THEME[k];
  });
  return out;
}

function buildCss(t) {
  const v = inner(t);
  return `
:root{--brand:${t.brand};--brand-hover:${t.brand_hover};--app-bg:${t.background};--app-text:${t.text};}
html.ui-v2{--brand:${v.brand};--brand-hover:${v.brand_hover};--bg:${v.background};--text:${v.text};}
.bg-\\[\\#cc0033\\]{background-color:var(--brand)!important}
.text-\\[\\#cc0033\\]{color:var(--brand)!important}
.hover\\:bg-\\[\\#a30028\\]:hover{background-color:var(--brand-hover)!important}
html.ui-v2 .hover\\:bg-\\[\\#b3002d\\]:hover{background-color:var(--brand-hover)!important}
html:not(.ui-v2) .border-\\[\\#cc0033\\]{border-color:var(--brand)!important}
html:not(.ui-v2) .grid-border{border-color:var(--brand)!important}
html.ui-v2 .border-\\[\\#cc0033\\]{border-color:var(--line)!important}
html.ui-v2 .border-\\[\\#cc0033\\].bg-\\[\\#cc0033\\]{border-color:var(--brand)!important}
html:not(.ui-v2) body{background-color:var(--app-bg);color:var(--app-text)}
html.ui-v2 body{background-color:var(--bg);color:var(--text)}
html.ui-v2 .text-\\[\\#09090b\\],html.ui-v2 .text-black{color:var(--text)!important}
`;
}

function apply(t) {
  let el = document.getElementById("app-theme");
  if (!el) {
    el = document.createElement("style");
    el.id = "app-theme";
    document.head.appendChild(el);
  }
  el.textContent = buildCss(t);
}

// Ultimo tema scaricato: colori e logo compaiono subito all'apertura, poi si aggiorna in background
const THEME_CACHE = "theme_cache";
const cachedTheme = () => {
  try {
    return JSON.parse(localStorage.getItem(THEME_CACHE) || "null");
  } catch {
    return null;
  }
};
const storeTheme = (t) => {
  try {
    localStorage.setItem(THEME_CACHE, JSON.stringify(t));
  } catch {
    /* storage pieno o non disponibile */
  }
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => cachedTheme() || DEFAULT_THEME);

  useEffect(() => {
    const cached = cachedTheme();
    if (cached) apply(cached);
    (async () => {
      try {
        const { data } = await api.get("/theme");
        setTheme(data);
        apply(data);
        storeTheme(data);
      } catch {
        if (!cached) apply(DEFAULT_THEME);
      }
    })();
  }, []);

  const preview = useCallback((t) => apply(t), []);

  const save = useCallback(async (t) => {
    const { data } = await api.put("/theme", t);
    setTheme(data);
    apply(data);
    storeTheme(data);
    return data;
  }, []);

  const value = useMemo(
    () => ({ theme, setTheme, preview, save }),
    [theme, preview, save]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
